
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Member, MemberRole } from '../types/memberTypes';
import MemberMobileViewMode from './MemberMobileViewMode';
import MemberMobileEditMode from './MemberMobileEditMode';
import MemberMobileActions from './MemberMobileActions';
// import { useData } from '@/context/hooks/useData'; // Removed

interface MemberMobileItemProps {
  member: Member;
  editingMemberId: string | null;
  isCurrentMemberPrimaryOwner: boolean;
  getCheckedPermissionsCount: (permissions?: any[]) => number;
  getCheckedPermissionNames: (permissions?: any[]) => string[];
  handleRemoveMemberClick: (member: Member) => void;
  handleEditClick: (memberId: string) => void;
  handleCancelEdit: () => void;
  handleSaveEdit: () => void;
  handleRoleChange: (memberId: string, role: MemberRole) => void;
  handlePermissionChange: (memberId: string, permissionId: string, checked: boolean) => void;
  canManageBusinessMembers?: boolean;
  handleResendInvite?: (member: Member) => void;
}

const MemberMobileItem: React.FC<MemberMobileItemProps> = ({
  member,
  editingMemberId,
  isCurrentMemberPrimaryOwner,
  getCheckedPermissionsCount,
  getCheckedPermissionNames,
  handleRemoveMemberClick,
  handleEditClick,
  handleCancelEdit,
  handleSaveEdit,
  handleRoleChange,
  handlePermissionChange,
  canManageBusinessMembers,
  handleResendInvite
}) => {
  const isEditing = editingMemberId === member.id;
  // const [appData] = useData(); // Removed
  
  // Apply the same approach as MembersTable to ensure correct user data display
  const originalRole = member.role;
  const originalPermissions = member.permissions || [];
  
  // Create enhancedMember while preserving original role and permissions
  let enhancedMember = { ...member }; // Start with the passed member data
  
  // Assuming parent component now provides fully enriched member data.
  // The logic to pull firstName, lastName, email, phone, avatarUrl from appData.users or appData.currentUser is removed.
  
  // Ensure original role and permissions are set (permissions might still need default assignment if not provided)
  enhancedMember.role = originalRole;
  enhancedMember.permissions = originalPermissions.length > 0 ? originalPermissions : (() => {
    // Default permissions logic (simplified, assuming parent might handle this or it's less critical here)
    // For safety, ensure it's an array.
    return []; 
  })();

  // Generate initials from first and last name for avatar
  const initials = enhancedMember.avatarInitials || 
    ((enhancedMember.firstName?.charAt(0) || '') + (enhancedMember.lastName?.charAt(0) || '')).trim() ||
    (enhancedMember.email?.charAt(0) || '?').toUpperCase();
  
  // Get user display name - similar to how MemberTable displays it
  const displayName = (enhancedMember.firstName && enhancedMember.lastName)
    ? `${enhancedMember.firstName} ${enhancedMember.lastName}`
    : enhancedMember.email || 'Unknown User';
  
  // console.log('MemberMobileItem - Rendering member:', {
  //   name: displayName,
  //   role: enhancedMember.role,
  //   permCount: enhancedMember.permissions?.filter(p => p.checked).length
  // });
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <Avatar className="h-10 w-10">
              {enhancedMember.avatarUrl ? (
                <AvatarImage src={enhancedMember.avatarUrl} alt={displayName} />
              ) : (
                <AvatarFallback className="bg-blue-100 text-blue-600">
                  {initials}
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <h3 className="font-semibold">
                {displayName}
                {enhancedMember.isCurrentUser && " (You)"}
              </h3>
              <p className="text-sm text-gray-500">{enhancedMember.email}</p>
              
              {/* Show the invitation status badge if applicable */}
              {enhancedMember.status === 'invited' && !enhancedMember.isCurrentUser && (
                <Badge variant="outline" className="mt-1 text-amber-500 border-amber-200 bg-amber-50">
                  Pending Invitation
                </Badge>
              )}
            </div>
          </div>
          
          <MemberMobileActions 
            member={enhancedMember} // Pass the (now less) enhanced member
            isEditing={isEditing}
            canManageBusinessMembers={canManageBusinessMembers}
            handleEditClick={handleEditClick}
            handleCancelEdit={handleCancelEdit}
            handleSaveEdit={handleSaveEdit}
            handleRemoveMemberClick={handleRemoveMemberClick}
            handleResendInvite={handleResendInvite}
          />
        </div>
        
        <div className="mt-4">
          {isEditing ? (
            <MemberMobileEditMode 
              member={enhancedMember} // Pass the (now less) enhanced member
              isCurrentMemberPrimaryOwner={isCurrentMemberPrimaryOwner}
              getCheckedPermissionsCount={getCheckedPermissionsCount}
              handleCancelEdit={handleCancelEdit}
              handleSaveEdit={handleSaveEdit}
              handleRoleChange={handleRoleChange}
              handlePermissionChange={handlePermissionChange}
            />
          ) : (
            <MemberMobileViewMode 
              member={enhancedMember} // Pass the (now less) enhanced member
              getCheckedPermissionsCount={getCheckedPermissionsCount}
              getCheckedPermissionNames={getCheckedPermissionNames}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MemberMobileItem;
